import { colors, ui } from '@beelday/common'
import styled from '@emotion/styled'
import { useSelector } from 'app-redux'
import { useAuthenticatedBeeldayClient } from 'connectivity/beelday-hooks'
import { translate } from 'intl/translate'
import { useState } from 'react'
import { useAssertedJoinedRoomAddress } from 'room/common/use-joined-room-address'
import { useAssertedWorkflowUser } from 'room/common/use-workflow-user'
import ProgressingIceBreaker from 'room/training-room/ice-breaker/presentation/progressing-ice-breaker'
import QuizRanking from 'room/training-room/ice-breaker/quiz-ranking'
import { QuizState, selectActiveQuiz } from 'room/training-room/redux'
import { quizQuestionOf } from './model/quiz'
import { QuizView } from './presentation/quiz-view'
import { useConfirmedSend } from './use-confirmed-send'

type QuestionProps = { quiz: QuizState }

/**
 * The shared ranking splits its parent's height between podium, list and
 * score. A phone held sideways leaves about 330px for all three and the list
 * collapses to nothing, so a short screen lays it out at a phone's height and
 * scrolls.
 */
const RankingFrame = styled.div`
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
	padding: 0 24px;

	@media (max-height: 500px) {
		flex: 0 0 auto;
		height: 560px;
	}
`

/**
 * One question. Keyed on the question number by the parent, so a pick or a
 * failed send never carries over to the next question.
 */
const ConfiguredQuizQuestion = ({
	quiz,
}: QuestionProps): JSX.Element | null => {
	const beeldayClient = useAuthenticatedBeeldayClient()
	const roomAddress = useAssertedJoinedRoomAddress()
	const me = useAssertedWorkflowUser()
	const question = quizQuestionOf(quiz, me.id)
	const confirmed = question?.options.some(option => option.mine) ? 1 : 0
	const { status, send } = useConfirmedSend(confirmed)
	const [picked, setPicked] = useState<string>()

	if (!question) {
		return null
	}

	return (
		<QuizView
			number={question.number}
			total={question.total}
			question={question.text}
			imageUrl={
				question.imageId
					? beeldayClient.quizImgUrl(question.imageId)
					: undefined
			}
			options={question.options}
			outcome={question.outcome}
			picked={picked}
			sending={status === 'sending'}
			failed={status === 'failed'}
			onPick={setPicked}
			onConfirm={() => {
				if (!picked) {
					return
				}
				send(() =>
					beeldayClient.confirmQuizAnswer({
						...roomAddress,
						answerId: picked,
						quizId: quiz.quizId,
					})
				)
			}}
		/>
	)
}

/**
 * The icebreaker quiz on the phone: questions in the phone's own layout, the
 * shared ranking once the quiz is over.
 */
export const ConfiguredQuiz = (): JSX.Element => {
	const quiz = useSelector(selectActiveQuiz)

	if (!quiz) {
		return <ui.FullPageSpinner color={colors.indigoBlue} />
	}

	if (quiz.phase === 'quiz-concluded') {
		return (
			<RankingFrame>
				<ProgressingIceBreaker title={translate('quiz.ranking')}>
					<QuizRanking />
				</ProgressingIceBreaker>
			</RankingFrame>
		)
	}

	return <ConfiguredQuizQuestion key={quiz.state.number} quiz={quiz} />
}

export default ConfiguredQuiz
